import React, { FC } from 'react';

import { Box, useDisclosure, HStack, Center } from '@chakra-ui/react';
import { DateRange, DayPicker, DropdownProps } from 'react-day-picker';
import { colorOpacity, customTheme } from 'theme';

import { dateLabel } from 'components/DataDisplay';
import { CustomSpinner } from 'components/DataDisplay/Spinner/CustomSpinner';
import { PrimaryButton, PrimaryIconButton, Select } from 'components/DataEntry';
import { CustomDayPickerProps } from 'components/DataEntry/Date/types';
import { CalendarIcon } from 'components/Icons';
import { Popover } from 'components/Overlay';

import { defaultStyles } from './styles';

const CalendarDropdown = (props: DropdownProps): React.ReactElement => {
  const {
    options = [],
    value,
    disabled,
    onChange,
    'aria-label': ariaLabel,
  } = props;
  const selectOptions = options.map(option => ({
    label: option.label,
    value: option.value,
    isDisabled: option.disabled,
  }));
  const selectedOption = selectOptions.find(option => option.value === value);

  return (
    <Box>
      <Select
        aria-label={ariaLabel}
        options={selectOptions}
        value={selectedOption}
        isDisabled={disabled}
        showIconInValueContainer={false}
        showClearIndicator={false}
        showDropdownIndicator={false}
        onChange={option =>
          onChange?.({
            target: { value: option.value },
          } as React.ChangeEvent<HTMLSelectElement>)
        }
        showBorder={false}
      />
    </Box>
  );
};

export const CustomDayPicker: FC<CustomDayPickerProps> = props => {
  const {
    label,
    button,
    dateProps,
    styles,
    isLoading = false,
    portal = false,
    showValue = '',
    iconButton = false,
    icon,
    iconButtonProps,
    isDisabled,
    header,
    footer,
    popoverDisclosure,
    initialFocusRef,
    isLazy = false,
  } = props;
  const { isOpen, onOpen, onClose } = useDisclosure();
  const currentMonth = new Date();
  const currentYear = currentMonth.getFullYear();
  const selectedRangeBackground = colorOpacity(
    customTheme.colors.primary,
    0.12,
  );

  const getValue = () => {
    const mode = dateProps?.mode;
    const selected = (dateProps as
      | { selected?: Date | Date[] | DateRange }
      | undefined)?.selected;

    if (!selected)
      return <Box color={customTheme.colors.gray['200']}>Choose date</Box>;
    if (showValue) return showValue;
    switch (mode) {
      case 'single':
        return dateLabel(selected);
      case 'multiple':
        return Array.isArray(selected) ? selected.length : 0;
      case 'range': {
        const range = selected as DateRange;
        if (!range.from) return 'Choose date';
        if (!range.to) return dateLabel(range.from);
        return `${dateLabel(range.from)} - ${dateLabel(range.to)}`;
      }
    }
  };

  const onClickButton = () => {
    if (isDisabled) return;

    if (popoverDisclosure?.onOpen) {
      popoverDisclosure.onOpen();
    } else {
      onOpen();
    }
  };

  return (
    <Box>
      <Popover
        styles={{ footer: { p: 0 }, ...styles?.popover }}
        isLazy={isLazy}
        isOpen={popoverDisclosure?.isOpen || isOpen}
        initialFocusRef={initialFocusRef}
        onClose={popoverDisclosure?.onClose ?? onClose}
        portal={portal}
        button={
          iconButton ? (
            <Box>
              <PrimaryIconButton
                aria-label="filters"
                onClick={onClickButton}
                icon={icon ?? <CalendarIcon />}
                tooltip={label}
                isDisabled={isDisabled}
                {...iconButtonProps}
              />
            </Box>
          ) : button ? (
            <PrimaryButton onClick={onClickButton} isDisabled={isDisabled}>
              {label ?? 'Select Date'}
            </PrimaryButton>
          ) : (
            <Box
              onClick={isDisabled ? undefined : onClickButton}
              {...defaultStyles.inputBox}
              {...styles?.inputBox}
              py={1}
              {...(isDisabled
                ? {
                    color: customTheme.colors.gray['200'],
                    cursor: 'not-allowed',
                  }
                : {
                    cursor: 'pointer',
                  })}
            >
              <HStack>
                <Center
                  boxSize={5}
                  color={
                    isDisabled ? customTheme.colors.gray['250'] : 'inherit'
                  }
                >
                  {isLoading ? (
                    <CustomSpinner loading={isLoading} />
                  ) : (
                    icon ?? <CalendarIcon />
                  )}
                </Center>
                <Box>{getValue()}</Box>
              </HStack>
            </Box>
          )
        }
        header={header}
        footer={footer}
        body={
          <Center
            sx={{
              '.rdp-caption_label > .rdp-chevron': {
                display: 'none',
              },
              '.rdp-root': {
                '--rdp-accent-color': customTheme.colors.primary,
                '--rdp-accent-background-color': selectedRangeBackground,
                '--rdp-selected-border': `1px solid ${customTheme.colors.primary}`,
                '--rdp-range_middle-background-color': selectedRangeBackground,
                '--rdp-range_middle-color': customTheme.colors.primary,
                '--rdp-range_start-color': customTheme.colors.primary,
                '--rdp-range_start-background': selectedRangeBackground,
                '--rdp-range_start-date-background-color': 'transparent',
                '--rdp-range_end-color': customTheme.colors.primary,
                '--rdp-range_end-background': selectedRangeBackground,
                '--rdp-range_end-date-background-color': 'transparent',
              },
              '.rdp-range_start': {
                borderTopLeftRadius: '999px',
                borderBottomLeftRadius: '999px',
              },
              '.rdp-range_end': {
                borderTopRightRadius: '999px',
                borderBottomRightRadius: '999px',
              },
              '.rdp-range_start.rdp-range_end': {
                borderRadius: '999px',
              },
              '.rdp-selected': {
                fontSize: 'inherit',
                fontWeight: 'inherit',
              },
            }}
            pt={1}
          >
            <DayPicker
              animate
              captionLayout="dropdown"
              navLayout="around"
              startMonth={new Date(currentYear - 100, 0)}
              endMonth={currentMonth}
              {...dateProps}
              components={{
                MonthsDropdown: CalendarDropdown,
                YearsDropdown: CalendarDropdown,
                ...dateProps?.components,
              }}
            />
          </Center>
        }
      />
    </Box>
  );
};
